import { AccountingSubType } from "@entities/accounting/lib/constants";
import { useGetInvoices } from "@entities/accounting/lib/hooks/useGetInvoices";
import { useGetInvoicesAggregates } from "@entities/accounting/lib/hooks/useGetInvoicesAggregates";
import { AddBrandAccounting } from "@features/AddBrandAccounting";
import { BriefAccountingTable } from "@features/BriefAccountingTable";
import { DownloadBilling } from "@features/DownloadBilling";
import type {
  InvoiceDocType,
  InvoiceFiltersState,
} from "@features/FinanceInvoiceFilters";
import { FinanceInvoiceFilters } from "@features/FinanceInvoiceFilters";
import { FinanceMetrics } from "@features/FinanceMetrics";
import { Modal } from "@shared/ui";
import { useEffect, useMemo, useState } from "react";
import { useDebounce } from "use-debounce";

const defaultFilters: InvoiceFiltersState = {
  brandIds: [],
  paymentStatuses: [],
  briefIds: [],
};

export function FinanceInvoices() {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 1000);
  const [docType, setDocType] = useState<InvoiceDocType>("All");
  const [filters, setFilters] = useState<InvoiceFiltersState>(defaultFilters);
  const [isAddBillingOpen, setIsAddBillingOpen] = useState(false);
  const [isDownloadBillingOpen, setIsDownloadBillingOpen] = useState(false);

  const subTypes = useMemo((): AccountingSubType[] | undefined => {
    if (docType === "All") return undefined;
    return [docType as AccountingSubType];
  }, [docType]);

  const { invoices, isFetching, pagination, fetchNextPage, hasNextPage } =
    useGetInvoices({
      searchQuery: debouncedSearchQuery,
      limit: 10,
      brandIds:
        filters.brandIds.length > 0
          ? filters.brandIds.map((b) => b.id)
          : undefined,
      briefIds:
        filters.briefIds.length > 0
          ? filters.briefIds.map((b) => b.id)
          : undefined,
      paymentStatuses:
        filters.paymentStatuses.length > 0
          ? filters.paymentStatuses
          : undefined,
      accountingDate: filters.accountingDate,
      subTypes,
    });

  const {
    invoicesAggregates,
    isInvoicesAggregatesFetching,
    isInvoicesAggregatesSuccess,
    invoicesAggregatesError,
  } = useGetInvoicesAggregates({
    searchQuery: debouncedSearchQuery,
    brandIds:
      filters.brandIds.length > 0
        ? filters.brandIds.map((b) => b.id)
        : undefined,
    briefIds:
      filters.briefIds.length > 0
        ? filters.briefIds.map((b) => b.id)
        : undefined,
    paymentStatuses:
      filters.paymentStatuses.length > 0 ? filters.paymentStatuses : undefined,
    accountingDate: filters.accountingDate,
    subTypes,
  });

  useEffect(() => {
    if (docType) {
      setFilters(defaultFilters);
    }
  }, [docType]);

  return (
    <div>
      <FinanceMetrics
        paymentOverdue={invoicesAggregates?.data?.paymentsOverdue || 0}
        creditsGiven={invoicesAggregates?.data?.creditsGiven || 0}
        leftToInvoice={invoicesAggregates?.data?.leftToInvoice || 0}
      />

      <BriefAccountingTable
        invoices={invoices}
        isFetching={isFetching}
        pagination={pagination}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage ?? false}
        invoicingAggregates={invoicesAggregates}
        title={<FinanceInvoiceFilters
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        docType={docType}
        setDocType={setDocType}
        filters={filters}
        setFilters={setFilters}
        onExportCsv={() => {}}
        onAddBilling={() => setIsAddBillingOpen(true)}
        onDownloadBilling={() => setIsDownloadBillingOpen(true)}
      />}
      />
      <AddBrandAccounting
        isOpen={isAddBillingOpen}
        onClose={() => setIsAddBillingOpen(false)}
      />
      <Modal
        title="Download Billing"
        destroyOnClose
        footer={null}
        width={500}
        open={isDownloadBillingOpen}
        onCancel={() => setIsDownloadBillingOpen(false)}
      >
        {isDownloadBillingOpen && (
          <DownloadBilling onClose={() => setIsDownloadBillingOpen(false)} />
        )}
      </Modal>
    </div>
  );
}
